var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { useMemoize, watchImmediate } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { loadWorkflows } from "@/components/Workflow/workflows.services";
import { useAnimationFrameScroll } from "@/composables/sensors/animationFrameScroll";
import { useToast } from "@/composables/toast";
import ActivityPanel from "./ActivityPanel.vue";
import FavoritesButton from "./Buttons/FavoritesButton.vue";
import DelayedInput from "@/components/Common/DelayedInput.vue";
import ScrollToTopButton from "@/components/ToolsList/ScrollToTopButton.vue";
import WorkflowCardList from "@/components/Workflow/List/WorkflowCardList.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowPanel',
    props: {
        currentWorkflowId: null
    },
    emits: ["insertWorkflow", "insertWorkflowSteps"],
    setup(__props, { emit }) {
        const props = __props;
        const scrollable = ref(null);
        const { arrived, scrollTop } = useAnimationFrameScroll(scrollable);
        const loading = ref(false);
        const totalWorkflowsCount = ref(Infinity);
        const allLoaded = computed(() => totalWorkflowsCount.value <= workflows.value.length);
        const filterText = ref("");
        const workflows = ref([]);
        const showFavorites = computed({
            get() {
                return filterText.value.includes("is:bookmarked");
            },
            set(value) {
                if (value) {
                    if (!filterText.value.includes("is:bookmarked")) {
                        filterText.value = `is:bookmarked ${filterText.value}`.trim();
                    }
                }
                else {
                    filterText.value = filterText.value.replace("is:bookmarked", "").trim();
                }
            },
        });
        const loadWorkflowsOptions = {
            sortBy: "update_time",
            sortDesc: true,
            limit: 20,
            showPublished: true,
            skipStepCounts: false,
        };
        const { error } = useToast();
        const getWorkflows = useMemoize((filterText, offset) => __awaiter(this, void 0, void 0, function* () {
            const { data, totalMatches } = yield loadWorkflows(Object.assign(Object.assign({}, loadWorkflowsOptions), { offset,
                filterText }));
            return { data, totalMatches };
        }));
        let fetchKey = "";
        let lastFetchKey = "";
        function load() {
            return __awaiter(this, void 0, void 0, function* () {
                const isCurrentFetch = () => fetchKey.trim().toLowerCase() === lastFetchKey.trim().toLowerCase();
                if (isCurrentFetch() && (loading.value || allLoaded.value)) {
                    return;
                }
                lastFetchKey = fetchKey;
                loading.value = true;
                try {
                    const { data, totalMatches } = yield getWorkflows(filterText.value, workflows.value.length);
                    if (isCurrentFetch()) {
                        totalWorkflowsCount.value = totalMatches;
                        const workflowIds = new Set(workflows.value.map((w) => w.id));
                        const newWorkflows = data.filter((w) => !workflowIds.has(w.id));
                        workflows.value.push(...newWorkflows);
                    }
                }
                catch (e) {
                    if (isCurrentFetch()) {
                        error(`Failed to load workflows: ${e}`);
                    }
                }
                finally {
                    if (isCurrentFetch()) {
                        loading.value = false;
                    }
                }
            });
        }
        function resetWorkflows() {
            workflows.value = [];
            totalWorkflowsCount.value = Infinity;
            loading.value = false;
            fetchKey = "";
        }
        function refresh() {
            resetWorkflows();
            getWorkflows.clear();
            load();
        }
        watchImmediate(() => filterText.value, (newFilterText) => {
            showFavorites.value = newFilterText.includes("is:bookmarked");
            resetWorkflows();
            fetchKey = filterText.value;
            load();
        });
        watch(() => arrived.bottom, () => {
            if (arrived.bottom) {
                load();
            }
        });
        function scrollToTop() {
            var _a;
            (_a = scrollable.value) === null || _a === void 0 ? void 0 : _a.scrollTo({ top: 0, behavior: "smooth" });
        }
        return { __sfc: true, props, emit, scrollable, arrived, scrollTop, loading, totalWorkflowsCount, allLoaded, filterText, workflows, showFavorites, loadWorkflowsOptions, error, getWorkflows, fetchKey, lastFetchKey, load, resetWorkflows, refresh, scrollToTop, ActivityPanel, FavoritesButton, DelayedInput, ScrollToTopButton, WorkflowCardList };
    }
});
